import React from "react";
import type { PropertyInfoForDelete } from "@/ui/organisms/dealTypes";
import Typography from "@/ui/atoms/Typography";
import Box from "@/ui/atoms/Box";
import TextField from "@/ui/atoms/TextField";
import { Button, Modal } from "@proda-ai/ui-kit";
import Loader from "@/ui/atoms/Loader";

type DeletePropertyModalProps = {
    showModal: boolean;
    deleteInfo: PropertyInfoForDelete | null;
    isDeleteInfoPending: boolean;
    onConfirm: (propertyId: number) => void;
    onCancel: () => void;
};

export const DeletePropertyModal = ({
    showModal,
    deleteInfo,
    isDeleteInfoPending,
    onConfirm,
    onCancel,
}: DeletePropertyModalProps) => {
    const modalTitle = "Are you sure you want to delete the property?";

    const [confirmation, setConfirmation] = React.useState("");
    // No deleteInfo after loading = the info query failed; keep Delete disabled
    // rather than showing an enabled button whose click silently no-ops.
    const deleteInfoFailed = !isDeleteInfoPending && deleteInfo === null;
    const canConfirm =
        deleteInfo !== null &&
        (confirmation.trim() === "delete" || !deleteInfo.hasCompletedRr);

    const renderFooter = () => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={onCancel} pattern="secondary" size="medium">
                Cancel
            </Button>
            <Button
                onClick={() => {
                    if (deleteInfo !== null) onConfirm(deleteInfo.propertyId);
                }}
                pattern="danger"
                disabled={!canConfirm}
                size="medium"
            >
                Delete
            </Button>
        </Box>
    );

    return (
        <Modal
            open={showModal}
            headerTitle={modalTitle}
            onCancel={onCancel}
            onClose={onCancel}
            footer={renderFooter()}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {isDeleteInfoPending ? (
                    <Loader />
                ) : deleteInfoFailed ? (
                    <Typography variant="body2">
                        Could not load the property&apos;s deletion details.
                        Please close this dialog and try again.
                    </Typography>
                ) : deleteInfo && deleteInfo.hasCompletedRr ? (
                    <>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography variant="body2">
                                There is one associated Rent Roll which will be
                                deleted.
                            </Typography>
                            <Typography variant="body2">
                                <Box
                                    component="span"
                                    sx={{ color: "var(--proda-red)" }}
                                >
                                    Important:
                                </Box>{" "}
                                There is one associated Rent Roll uploaded with
                                other properties, and it will also be deleted.
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Typography variant="body2">
                                Type &quot;delete&quot; to{" "}
                                <Box
                                    component="span"
                                    sx={{ color: "var(--proda-red)" }}
                                >
                                    permanently delete
                                </Box>{" "}
                                this property:
                            </Typography>
                            <TextField
                                name="confirmation"
                                value={confirmation}
                                onChange={e =>
                                    setConfirmation(
                                        (e.target as HTMLInputElement).value,
                                    )
                                }
                                sx={{ width: "260px" }}
                            />
                        </Box>
                    </>
                ) : (
                    <Typography variant="body2">
                        This action cannot be undone.
                    </Typography>
                )}
            </Box>
        </Modal>
    );
};
